/**
 * Report the resolved color scheme (NM-M7B3CD, NM-DV3RKT).
 *
 * kerf UI's palette resolves through `light-dark()` and follows `color-scheme`,
 * which `ui-theme.css` pins from the server-stamped `data-theme` (FR-3.76) and
 * otherwise leaves to the system. The app bar's brand tint still has to declare
 * the scheme it sits in, so this tells `onChange` the resolved scheme now and on
 * every change: a re-pin, or a system change under `auto`. (It also stamped Web
 * Awesome's dark and light appearance classes until Web Awesome was removed,
 * NM-DRDNC3.)
 *
 * Returns a disposer.
 */
export function syncColorScheme(
  doc: Document,
  win: Window,
  /** Told the resolved scheme on every change. */
  onChange: (dark: boolean) => void,
): () => void {
  const root = doc.documentElement;
  const media = win.matchMedia('(prefers-color-scheme: dark)');
  const apply = (): void => {
    const pinned = root.getAttribute('data-theme');
    onChange(pinned === 'dark' || (pinned !== 'light' && media.matches));
  };
  apply();
  media.addEventListener('change', apply);
  const observer = new MutationObserver(apply);
  observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
  return () => {
    media.removeEventListener('change', apply);
    observer.disconnect();
  };
}
